/**
 * Copyright (c) 2017 Interacso
 * All rights reserved.
 */

import React, { ReactElement } from 'react';
import { injectIntl, InjectedIntl } from 'react-intl';
import { fetchGeolocation } from '../../../../../../../utils';
import { getTextContent } from '../../../../../../../utils/creativity';
import { WeatherIcon } from '../../../../../../../components';
import { Condition } from '../../../../../../../components/WeatherIcon';
import { Element, ElementProps } from '../Element';
import '../styles';

interface ElementExtraProps {
  feature: any;
  dateUpdate: any;
  location: string;
}

interface ExtraProps {
  intl: InjectedIntl;
}

interface ElementExtraState {
  geo?: any;
  error?: any;
}

interface ICondition {
  high: number;
  low: number;
}

interface IForecast {
  [date: string]: {
    temperature: {
      high: number;
      low: number;
    };
    condition: Condition;
  };
}

type Props = ElementProps<ElementExtraProps> & ExtraProps;

/**
 * Elemento de tipo clima
 */
class WeatherElement extends Element<
  ElementExtraProps,
  ElementExtraState,
  ExtraProps
> {
  constructor(props: any) {
    super(props);

    this.state = {
      geo: null,
    };
  }

  fetchTimeout?: NodeJS.Timeout;

  weatherForecast(
    forecast: IForecast,
    condToday: ICondition,
    date: string,
    feature: string
  ) {
    let high: number = 0;
    let low: number = 0;
    let condForecast: string = '';

    if (condToday) {
      high = condToday.high;
      low = condToday.low;
    }

    if (forecast && forecast.length && date) {
      high = forecast[date].temperature.high;
      low = forecast[date].temperature.low;
    }
    const condition = Number(date) && condForecast ? condForecast : condToday;

    switch (feature) {
      case 'high':
        return `${Math.round(high)}º`;
      case 'low':
        return `${Math.round(low)}º`;
      case 'icon':
        return <WeatherIcon name={condition as Condition} />;
      case 'condition':
        return this.props.intl.formatMessage({ id: 'condition.' + condition });
      default:
        return '';
    }
  }

  getContent() {
    const { element } = this.props;
    const { feature, dateUpdate } = element;
    const { geo, error } = this.state;
    const features = [
      'temperature',
      'high',
      'low',
      'condition',
      'uv',
      'air',
      'humidity',
      'wind_dir',
      'wind_kph',
      'icon',
    ];
    let content: string | ReactElement<any> = '';

    if (error) {
      if (features.indexOf(feature) !== -1) {
        if (feature === 'icon') {
          return <WeatherIcon name="clear" />;
        } else {
          content = this.props.intl.formatMessage({ id: 'default.' + feature });
        }
      }
    }

    if (geo) {
      switch (feature) {
        case 'temperature':
          content =
            typeof geo.temperature === 'number'
              ? Math.round(geo.temperature) + 'º'
              : '';
          break;
        case 'high':
          content = this.weatherForecast(
            geo.forecast,
            geo.condition,
            dateUpdate,
            feature
          );
          break;
        case 'low':
          content = this.weatherForecast(
            geo.forecast,
            geo.condition,
            dateUpdate,
            feature
          );
          break;
        case 'condition':
          content = this.weatherForecast(
            geo.forecast,
            geo.condition,
            dateUpdate,
            feature
          );
          break;
        case 'uv':
          content = geo.uv;
          break;
        case 'air':
          content = geo.air;
          break;
        case 'pollen':
          content = geo.pollen;
          break;
        case 'humidity':
          content = geo.humidity;
          break;
        case 'wind_dir':
          content = geo.wind_dir;
          break;
        case 'wind_kph':
          content =
            typeof geo.wind_kph === 'number'
              ? Math.round(geo.wind_kph) + 'km/h'
              : '';
          break;
        case 'icon':
          return this.weatherForecast(
            geo.forecast,
            geo.condition,
            dateUpdate,
            feature
          );
        default:
          break;
      }
    }

    return getTextContent({ ...element, content });
  }

  render() {
    return <div style={this.getStyle()}>{this.getContent()}</div>;
  }

  componentDidMount() {
    this.fetchGeo();
  }

  componentDidUpdate(prevProps: Props) {
    const { location } = this.props.element;

    if (location !== prevProps.element.location) {
      this.setState({ geo: undefined, error: undefined });
      if (this.fetchTimeout) {
        clearTimeout(this.fetchTimeout);
      }
      this.fetchTimeout = global.setTimeout(this.fetchGeo.bind(this), 1000);
    }
  }

  componentWillUnmount() {
    if (this.fetchTimeout) {
      clearTimeout(this.fetchTimeout);
    }
  }

  fetchGeo() {
    const { location, feature } = this.props.element;

    fetchGeolocation(location, feature)
      .then(json => this.setState({ geo: json }))
      .catch(err => this.setState({ error: err }));
  }
}

export default injectIntl(WeatherElement);
